import React, { useState, useEffect } from 'react';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useTranslation } from '../hooks/useTranslation';
import { BookCard } from './BookCard';
import { ManifestationCard } from './ManifestationCard';
import { BookData, books } from '../data/books';
import { manifestationCards } from '../data/manifestations';

export const BookLibrary: React.FC = () => {
  const { t } = useTranslation();
  const [category, setCategory] = useState<'all' | 'manifestation' | 'motivational'>('all');
  const [displayedItems, setDisplayedItems] = useState<Array<BookData | typeof manifestationCards[number]>>([]);
  const [nextManifestationIndex, setNextManifestationIndex] = useState(0);
  
  // Calculate if there are more items to load
  const bookCount = displayedItems.filter(item => 'title' in item).length;
  const filteredBooks = filterBooksByCategory(books, category);
  const hasMoreItems = bookCount < filteredBooks.length;
  
  const { loading, lastElementRef } = useInfiniteScroll(loadMoreItems, hasMoreItems);
  
  function filterBooksByCategory(books: BookData[], category: string): BookData[] {
    if (category === 'all') return books;
    return books.filter(book => book.category === category);
  }
  
  function addManifestationCards(books: BookData[], startingManifestationIndex: number): { 
    items: Array<BookData | typeof manifestationCards[number]>, 
    newManifestationIndex: number 
  } {
    const result: Array<BookData | typeof manifestationCards[number]> = [];
    let currentManifestationIndex = startingManifestationIndex;
    
    books.forEach((book, index) => {
      result.push(book);
      // Insert manifestation card every 2 books instead of every 3
      if ((index + 1) % 2 === 0 && currentManifestationIndex < manifestationCards.length) {
        result.push(manifestationCards[currentManifestationIndex]);
        currentManifestationIndex = (currentManifestationIndex + 1) % manifestationCards.length;
      }
    });
    
    return { items: result, newManifestationIndex: currentManifestationIndex };
  }
  
  // Initial load - increased from 8 to 12 books
  useEffect(() => {
    setNextManifestationIndex(0);
    const filtered = filterBooksByCategory(books, category);
    const { items: withManifestations, newManifestationIndex } = addManifestationCards(filtered.slice(0, 12), 0);
    setDisplayedItems(withManifestations);
    setNextManifestationIndex(newManifestationIndex);
  }, [category]);
  
  function loadMoreItems() {
    const filtered = filterBooksByCategory(books, category);
    const currentBookCount = displayedItems.filter(item => 'title' in item).length;
    
    if (currentBookCount >= filtered.length) {
      return;
    }
    
    // Increased batch size from 4 to 6 books
    const moreBooks = filtered.slice(currentBookCount, currentBookCount + 6);
    if (moreBooks.length > 0) {
      const { items: moreItems, newManifestationIndex } = addManifestationCards(moreBooks, nextManifestationIndex);
      setDisplayedItems(prev => [...prev, ...moreItems]);
      setNextManifestationIndex(newManifestationIndex);
    }
  }

  const hasReachedEnd = !hasMoreItems;

  return (
    <div className="w-full py-8">
      <div className="text-center mb-10 sm:mb-14">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-earth-stone mb-4 sm:mb-6 tracking-tight">{t('books.inspirationalLibrary')}</h2>
        <p className="text-base sm:text-lg lg:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-medium">
          {t('books.libraryDescription')}
        </p>
        
        <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mt-8 sm:mt-10">
          <button 
            onClick={() => setCategory('all')}
            className={`px-6 sm:px-8 py-3 sm:py-4 rounded-2xl text-sm sm:text-base font-semibold transition-all duration-300 shadow-md hover:shadow-xl transform hover:-translate-y-1
              ${category === 'all' ? 'bg-gradient-to-r from-earth-bronze to-earth-terracotta text-white' : 'bg-white text-gray-700 hover:bg-earth-sand border-2 border-earth-clay/30'}`}
          >
            {t('books.allBooks')}
          </button>
          <button 
            onClick={() => setCategory('manifestation')}
            className={`px-6 sm:px-8 py-3 sm:py-4 rounded-2xl text-sm sm:text-base font-semibold transition-all duration-300 shadow-md hover:shadow-xl transform hover:-translate-y-1
              ${category === 'manifestation' ? 'bg-gradient-to-r from-earth-bronze to-earth-terracotta text-white' : 'bg-white text-gray-700 hover:bg-earth-sand border-2 border-earth-clay/30'}`}
          >
            {t('books.manifestationCategory')}
          </button>
          <button 
            onClick={() => setCategory('motivational')}
            className={`px-6 sm:px-8 py-3 sm:py-4 rounded-2xl text-sm sm:text-base font-semibold transition-all duration-300 shadow-md hover:shadow-xl transform hover:-translate-y-1
              ${category === 'motivational' ? 'bg-gradient-to-r from-earth-bronze to-earth-terracotta text-white' : 'bg-white text-gray-700 hover:bg-earth-sand border-2 border-earth-clay/30'}`}
          >
            {t('books.motivationalCategory')}
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
        {displayedItems.map((item, index) => {
          // Create unique keys using index and type to avoid duplicates
          const uniqueKey = 'title' in item ? `book-${item.id}` : `manifest-${item.id}-${index}`;
          
          if (displayedItems.length === index + 1) {
            return (
              <div ref={lastElementRef} key={uniqueKey}>
                {'title' in item ? <BookCard book={item} /> : <ManifestationCard card={item} />}
              </div>
            );
          } else {
            return (
              <div key={uniqueKey}>
                {'title' in item ? <BookCard book={item} /> : <ManifestationCard card={item} />}
              </div>
            );
          }
        })}
      </div>
      
      {loading && (
        <div className="text-center mt-10 sm:mt-14">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-earth-bronze border-r-transparent"></div>
        </div>
      )}
      
      {hasReachedEnd && (
        <div className="text-center max-w-md mx-auto mt-10 sm:mt-14 pt-8 sm:pt-10 border-t-2 border-earth-clay/50">
          <span className="text-base sm:text-lg text-gray-700 font-medium">{t('books.discoveredAllWisdom')}</span>
        </div>
      )}
    </div>
  );
};